import { forwardRef, useState, useEffect } from "react";
import { CartItem, Payload, ReplaceCartPayload } from "modules/cart/types/cart";
import { StoreData } from "modules/main/types/storeData";
import {
  CartCreationStatus,
  emptyScenario,
  OptimizedScenario,
  StoreResume,
} from "modules/checkout/types/optimizedCart";
import { getStoreBdData } from "utils/storeDataManager";
import Amount from "utils/amount";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  BadgeCheck,
  CircleAlert,
  RefreshCcw,
  Scale,
  SearchX,
  Store,
  ThumbsUp,
  TriangleAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";

import { ErrorCode } from "modules/main/types/error";
import { Separator } from "@/components/ui/separator";
import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ProductTable } from "./productTable";

import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
// import { OptimizedCartCard, OptimizedCartCardMobile } from "./optimizedCartCard";
import { AddCartMethod } from "modules/cart/types/add-cart-methods";
import { Spinner } from "@/components/ui/spinner";
import { isDesktopSize } from "utils/is-desktop";
import { useMediaQuery } from "@/hooks/use-media-query";
import { getTotalsFromScenario } from "utils/scenarioUtils";
import { OptimizedCartCard } from "./optimizedCartCard";

interface ResumeStores {
  [key: number]: {
    count: number;
    total: number;
  };
}

interface CartTableDetailProps extends React.HTMLAttributes<HTMLDivElement> {
  selectedIndexScenario: number;
  optimizedScenarios?: OptimizedScenario[];
  currCart?: CartItem[];
  addDeletefromCart: (payload: Payload) => void;
  removeFromCart?: (payload: Payload) => void;
  setNoReplace: (productId: string, newMethod: AddCartMethod) => void;
  storeList: StoreData[];

  mode: "VIEW" | "RESULT";
  errorCode?: ErrorCode;
  isMobile?: boolean;
  loading?: boolean;

  setSelectedIndexScenario(value: number, loading: boolean): void;
  workingCart: CartItem[];
  setWorkingCart: (cart: CartItem[]) => void;
  hasUnsavedChanges: boolean;
  applyChanges: () => void;
  resetWorkingCart: () => void;

  createCartCallBack: (scenarioId: number, storeId?: number) => void;
  cartCreationStatus: CartCreationStatus[];
  setCartStatus: React.Dispatch<React.SetStateAction<CartCreationStatus[]>>;
}

export const CartTableDetail = forwardRef<HTMLDivElement, CartTableDetailProps>(
  ({ mode, storeList, className, currCart, ...props }, ref) => {
    const selectedScenario =
      props.optimizedScenarios &&
      props.optimizedScenarios.length > 0 &&
      props.selectedIndexScenario !== undefined
        ? props.optimizedScenarios[props.selectedIndexScenario]
        : emptyScenario;
    const { store_resume } = selectedScenario;
    const formattedDateTime = props.errorCode?.message
      ? new Date(props.errorCode.message)
          .toLocaleString("es-ES", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          })
          .replace(",", " a las")
      : "";

    const notFoundCart = props.workingCart
      .filter(
        (p) =>
          !p.met_conditions ||
          (p.met_conditions &&
            p.price_component.some((pc) => pc.no_met_conditions)),
      )
      .map((p) => {
        if (
          p.met_conditions &&
          p.price_component.some((pc) => pc.no_met_conditions)
        ) {
          // Keep only unavailable components for the "No disponibles" tab.
          return {
            ...p,
            price_component: p.price_component.filter(
              (pc) => pc.no_met_conditions,
            ),
          };
        }
        return p;
      });

    return !props.isMobile ? (
      <CartDetailTableDesktop
        noFoundCart={notFoundCart}
        cart={currCart ?? []}
        // addDeletefromCart={props.addDeletefromCart}
        storeList={storeList}
        mode={mode}
        className={className}
        formattedDateTime={formattedDateTime}
        loading={props.loading}
        store_resume={store_resume}
        {...props}
      />
    ) : (
      <CartDetailTableMobile
        cart={currCart ?? []}
        noFoundCart={notFoundCart}
        // addDeletefromCart={props.addDeletefromCart}
        storeList={storeList}
        mode={mode}
        className={className}
        formattedDateTime={formattedDateTime}
        loading={props.loading}
        store_resume={store_resume}
        {...props}
      />
    );
  },
);

interface CartDetailTableProps extends CartTableDetailProps {
  cart: CartItem[];
  noFoundCart: CartItem[];
  storeList: StoreData[];
  formattedDateTime: string;
  store_resume: StoreResume[];
  cartCreationStatus: CartCreationStatus[];
  setCartStatus: React.Dispatch<React.SetStateAction<CartCreationStatus[]>>;
}

const CartDetailTableDesktop = forwardRef<HTMLDivElement, CartDetailTableProps>(
  (
    {
      className,
      optimizedScenarios,
      mode,
      cart,
      storeList,
      store_resume,
      ...props
    },
    ref,
  ) => {
    const selectedScenario =
      optimizedScenarios && optimizedScenarios.length > 0
        ? optimizedScenarios[props.selectedIndexScenario]
        : emptyScenario;
    const { cheapestStoreId: selectedStoreTab } = getTotalsFromScenario(
      selectedScenario,
      selectedScenario.scenarioType === "UNIQUE_STORE",
      false,
    );
    const numberStores = store_resume.length;

    return (
      <Card className={cn("py-8 px-6", className)}>
        <CardHeader className="gap-3">
          <CardTitle>
            <div className="flex flex-row justify-between items-center w-full">
              <h2 className="text-2xl font-bold">
                {numberStores > 1
                  ? "Tu Estrategia de Compra"
                  : "Comprando en una sola tienda"}
              </h2>
            </div>
          </CardTitle>
          <CardDescription>
            <span>
              {numberStores > 1
                ? "Te mostramos cómo ahorrar distribuyendo tu carro o comprando en el súper más barato"
                : "Recibe todo en un solo despacho " +
                  (props.noFoundCart.length > 0
                    ? ", aunque falten algunos productos"
                    : "")}
            </span>
          </CardDescription>
          <CardAction>
            {props.hasUnsavedChanges && (
              <Button
                variant={"destructive"}
                disabled={props.loading}
                onClick={props.applyChanges}
              >
                {props.loading ? (
                  <Spinner />
                ) : (
                  <RefreshCcw className="w-4 h-4" />
                )}{" "}
                ¡Actualizar!
              </Button>
            )}
          </CardAction>
          {selectedScenario.alert && (
            <MinAmountAlert
              storeResume={selectedScenario.store_resume}
              storeList={storeList}
            />
          )}
        </CardHeader>
        <CardContent>
          {/* {
            showSuggestion&&(
                <SuggestionsDesktop order_id={props.order_id??0} cart={cart} 
                    addDeletefromCart={props.addDeletefromCart ?? (() => {})}
                    removeFromCart={props.removeFromCart ?? (() => {})}
                    />)}             */}
          {
            <div className="flex flex-row gap-6 flex-wrap justify-center">
              {optimizedScenarios && optimizedScenarios.length > 0 && (
                <OptimizedCartCard
                  scenario={optimizedScenarios[0]}
                  createCartCallBack={props.createCartCallBack}
                />
              )}
            </div>
          }
          <div className="flex flex-col gap-4">
            {selectedScenario.scenarioType === "UNIQUE_STORE" ? (
              <p className="text-xl font-semibold mt-6">
                Detalle de tu carrito
              </p>
            ) : (
              <p className="text-xl font-semibold mt-6">
                Cómo se distribuye tu compra.
              </p>
            )}

            <CartProductTabs
              opt_cart={props.workingCart}
              noFoundCart={props.noFoundCart}
              loading={props.loading}
              store_resume={store_resume}
              disableButtons={true}
              storeList={storeList}
              mode="RESULT"
              setNoReplace={props.setNoReplace}
              cartCreationStatus={props.cartCreationStatus}
              setCartStatus={props.setCartStatus}
              filterByStoreId={(storeId) =>
                selectedScenario.scenarioType === "UNIQUE_STORE"
                  ? storeId === selectedStoreTab
                    ? storeId
                    : undefined
                  : storeId
              }
              addDeletefromCart={props.addDeletefromCart}
            />
          </div>
        </CardContent>
      </Card>
    );
  },
);

const CartDetailTableMobile = forwardRef<HTMLDivElement, CartDetailTableProps>(
  ({ mode, cart, store_resume, storeList, ...props }, ref) => {
    const selectedScenario =
      props.optimizedScenarios && props.optimizedScenarios.length > 0
        ? props.optimizedScenarios[props.selectedIndexScenario]
        : emptyScenario;
    const { cheapestStoreId: selectedStoreTab } = getTotalsFromScenario(
      selectedScenario,
      selectedScenario.scenarioType === "UNIQUE_STORE",
      false,
    );

    const numStores = store_resume.length;

    return (
      <div className="flex flex-col gap-2">
        <div className="flex flex-row justify-between">
          <div className="text-sm flex flex-col gap-0">
            <div className="flex flex-row justify-between  items-center">
              <p className="font-semibold">
                {numStores > 1
                  ? `Tu Estrategia de Compra`
                  : "Comprando en una sola tienda"}
              </p>
            </div>

            <p className="text-muted-foreground">
              {numStores > 1
                ? "Como se distribuye tu compra."
                : "Conveniente, un solo despacho."}
            </p>
          </div>
          {props.hasUnsavedChanges && (
            <span className="text-white">
              <Button
                variant={"destructive"}
                size={"sm"}
                disabled={props.loading}
                onClick={props.applyChanges}
                className="text-xxs"
              >
                {props.loading ? (
                  <Spinner />
                ) : (
                  <RefreshCcw className="w-4 h-4" />
                )}{" "}
                Actualizar!
              </Button>
            </span>
          )}
        </div>
        {props.loading &&
          [...Array(4)].map((_, index) => {
            return (
              <React.Fragment key={`${index}`}>
                <ProductTableItemSkeleton />
                {!(index === 3) && <Separator className="w-full p-0" />}
              </React.Fragment>
            );
          })}
        {selectedScenario.alert && (
          <MinAmountAlert
            storeResume={selectedScenario.store_resume}
            storeList={storeList}
          />
        )}
        {!props.loading && (
          <>
            {props.optimizedScenarios &&
              props.optimizedScenarios.length > 0 && (
                <OptimizedCartCard scenario={props.optimizedScenarios[0]} />
              )}

            <CartProductTabs
              opt_cart={props.workingCart}
              noFoundCart={props.noFoundCart}
              store_resume={store_resume}
              storeList={storeList}
              mode={mode}
              cartCreationStatus={props.cartCreationStatus}
              setCartStatus={props.setCartStatus}
              filterByStoreId={(storeId) =>
                selectedScenario.scenarioType === "UNIQUE_STORE"
                  ? storeId === selectedStoreTab
                    ? storeId
                    : undefined
                  : storeId
              }
              // disbaleButtons={selectedScenario.scenarioType==="UNIQUE_STORE"}
              disableButtons={true}
              setNoReplace={props.setNoReplace}
              addDeletefromCart={props.addDeletefromCart}
            />
          </>
        )}
      </div>
    );
  },
);

const ProductTableItemSkeleton = () => {
  return (
    <div className="flex w-full flex-row gap-4 items-start">
      <div>
        <Skeleton className="w-20 h-20" />
      </div>
      <div className="flex flex-col gap-2 w-full items-start">
        <div className="flex flex-row gap-4 items-center w-full">
          <div className="flex flex-col text-xs w-full gap-2">
            <Skeleton className="w-full h-4" />
            <div className="flex gap-2 w-full justify-between">
              <Skeleton className="w-12 h-4" />
              <Skeleton className="w-20 h-4" />
            </div>
            <Separator className="w-full p-0" />
            <div className="flex flex-row w-full gap-2 justify-between">
              <Skeleton className="w-16 h-4" />
              <Skeleton className="w-28 h-4" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface MinAmountAlertProps extends React.HTMLAttributes<HTMLDivElement> {
  storeResume?: StoreResume[];
  storeList: StoreData[];
}

const MinAmountAlert = forwardRef<HTMLDivElement, MinAmountAlertProps>(
  ({ ...props }, ref) => {
    return (
      <Alert variant={"default"} className="2xl:w-[80%] mx-auto">
        <div className="flex gap-4">
          <TriangleAlert className="h-6 w-6" />
          <div>
            <AlertTitle>Hay algunos problemas</AlertTitle>
            <AlertDescription className="flex flex-col gap-2 py-2">
              {/* <span>No se han cumplido los minimos de compra para las siguientes tiendas, por lo que no se han incluido en la compra optimizada. Prueba incluir más productos de esa tienda o busca una alternativa en otra.</span> */}
              {props.storeResume
                ?.filter((sr) => !sr.met_conditions)
                .map((si, i) => {
                  return (
                    <div
                      key={i}
                      className="flex gap-2 text-foreground items-center flex-wrap justify-center"
                    >
                      <div className="flex flex-row gap-2 items-center">
                        <Avatar className="w-8 h-8">
                          <AvatarImage
                            src={
                              getStoreBdData(si.id, props.storeList).logo_url
                            }
                          />
                        </Avatar>
                        <span className="max-w-24 line-clamp-1 break-all text-sm">
                          {getStoreBdData(si.id, props.storeList).name}
                        </span>
                      </div>
                      <div className="text-destructive font-semibold">
                        El Minimo de compra es{" "}
                        <Amount
                          className="ml-0 font-semibold"
                          amount={si.min_purchase}
                          currency="CLP"
                          locale="es-CL"
                        />{" "}
                        <span className="text-muted-foreground ml-1">
                          Lleva más productos o busca una alternativa
                        </span>
                      </div>
                    </div>
                  );
                })}
            </AlertDescription>
          </div>
        </div>
      </Alert>
    );
  },
);

interface CartProductTabsProps extends React.HTMLAttributes<HTMLDivElement> {
  store_resume: StoreResume[];
  mode: "VIEW" | "RESULT";
  opt_cart: CartItem[];
  noFoundCart?: CartItem[];
  storeList: StoreData[];
  filterByStoreId?: (storeId: number) => number | undefined;
  loading?: boolean;
  disableButtons: boolean;
  setNoReplace: (productId: string, newMethod: AddCartMethod) => void;
  addDeletefromCart: (payload: Payload) => void;
  cartCreationStatus: CartCreationStatus[];
  setCartStatus: React.Dispatch<React.SetStateAction<CartCreationStatus[]>>;
}

export const CartProductTabs = forwardRef<HTMLDivElement, CartProductTabsProps>(
  (
    { opt_cart, store_resume, storeList, mode, setNoReplace, ...props },
    ref,
  ) => {
    const isDesktop = useMediaQuery(isDesktopSize);
    const filteredStores = store_resume.filter(
      (store) =>
        !props.filterByStoreId || props.filterByStoreId(store.id) !== undefined,
    );
    const unavailableTabValue = "unavailable";
    const hasUnavailableProducts = (props.noFoundCart?.length ?? 0) > 0;
    const unavailableUnitsCount = (props.noFoundCart ?? []).reduce(
      (total, product) => {
        const unavailableUnitsFromComponents = product.price_component
          .filter((pc) => pc.no_met_conditions)
          .reduce((subtotal, pc) => subtotal + pc.quantity, 0);
        const fallbackUnits = product.price_component.reduce(
          (maxUnits, pc) => Math.max(maxUnits, pc.quantity),
          0,
        );

        return (
          total +
          (unavailableUnitsFromComponents > 0
            ? unavailableUnitsFromComponents
            : fallbackUnits)
        );
      },
      0,
    );
    const defaultTabValue =
      filteredStores.length > 0
        ? "0"
        : hasUnavailableProducts
          ? unavailableTabValue
          : "0";

    const buildFilteredCartByStore = (
      storeId: number,
      noMetConditions: boolean,
    ) => {
      const filtered = opt_cart
        .filter((product) => product.met_conditions)
        .map((product) => ({
          ...product,
          price_component: product.price_component.filter(
            (pc) =>
              pc.id === storeId && !!pc.no_met_conditions === noMetConditions,
          ),
        }))
        .filter((product) => product.price_component.length > 0);

      // Prioriza productos con reemplazo sugerido para mostrarlos primero.
      return filtered.sort((left, right) => {
        const leftHasReplacement = left.price_component.some((pc) =>
          Boolean(pc.replaced_name),
        );
        const rightHasReplacement = right.price_component.some((pc) =>
          Boolean(pc.replaced_name),
        );
        return Number(rightHasReplacement) - Number(leftHasReplacement);
      });
    };

    return (
      <Tabs defaultValue={defaultTabValue} className="w-full" ref={ref}>
        <div
          className={cn(
            "sticky top-16 lg:top-16 z-40 isolate bg-background border-b before:content-[''] before:absolute before:-top-2 before:left-0 before:right-0 before:h-2 before:bg-background",
            {
              "overflow-x-auto overflow-y-hidden": !isDesktop,
              "overflow-visible": isDesktop,
            },
          )}
        >
          <TabsList
            className={cn("justify-start w-fit", {
              "w-max whitespace-nowrap": !isDesktop,
              "overflow-visible": isDesktop,
              "min-h-20 py-3": isDesktop,
              "min-h-14 py-1": !isDesktop,
            })}
          >
            {filteredStores.map((store, index) => {
              const resumePerStore = opt_cart.reduce<ResumeStores>(
                (acc, item) => {
                  item.price_component
                    .filter((pc) => !pc.no_met_conditions)
                    .forEach((pc) => {
                      const { id, price, quantity } = pc;
                      if (!acc[id]) {
                        acc[id] = {
                          count: 0,
                          total: 0,
                        };
                      }
                      acc[id].count += quantity;
                      acc[id].total += price;
                    });
                  return acc;
                },
                {},
              );

              return (
                <TabsTrigger
                  value={index.toString()}
                  key={store.id}
                  asChild
                  className={cn(
                    "text-muted-foreground text-sm min-h-fit items-center justify-start cursor-pointer",
                    {
                      "!flex-none w-auto items-start min-w-60": isDesktop,
                      "!flex-none w-auto shrink-0": !isDesktop,
                    },
                  )}
                >
                  <div>
                    <Avatar
                      className={cn({
                        "w-8 h-8 mr-1 my-2": isDesktop,
                        "w-5 h-5 my-1": !isDesktop,
                      })}
                    >
                      <AvatarImage
                        src={getStoreBdData(store.id, storeList)?.logo_url}
                      />
                    </Avatar>
                    <div
                      className={cn("flex flex-col", { "text-xs": !isDesktop })}
                    >
                      <span className="font-semibold text-foreground">
                        {getStoreBdData(store.id, storeList)?.name}
                      </span>
                      {isDesktop && (
                        <div>
                          <span className="font-semibold text-foreground">
                            {resumePerStore[store.id]?.count || 0}
                          </span>{" "}
                          productos{" "}
                          <Amount
                            amount={resumePerStore[store.id]?.total || 0}
                            currency="CLP"
                            locale="es-CL"
                            className="font-semibold text-foreground"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </TabsTrigger>
              );
            })}
            {hasUnavailableProducts && (
              <TabsTrigger
                value={unavailableTabValue}
                className={cn(
                  "text-muted-foreground text-sm min-h-fit items-center justify-start cursor-pointer !flex-none w-auto",
                  { "min-w-44": isDesktop, "shrink-0": !isDesktop },
                )}
              >
                <div className={cn("flex flex-col", { "text-xs": !isDesktop })}>
                  <span className="font-semibold text-foreground inline-flex items-center gap-1.5">
                    <SearchX
                      className={cn("shrink-0", {
                        "w-4 h-4": isDesktop,
                        "w-3.5 h-3.5": !isDesktop,
                      })}
                    />
                    No disponibles
                  </span>
                  <span>
                    <span className="font-semibold text-foreground">
                      {unavailableUnitsCount}
                    </span>{" "}
                    unidades
                  </span>
                </div>
              </TabsTrigger>
            )}
          </TabsList>
        </div>
        {filteredStores.map((store, index) => {
          return (
            <TabsContent value={index.toString()} key={store.id}>
              {/* <Separator className="my-2"/>                         */}
              <ProductTable
                className="mt-0"
                cart={opt_cart}
                filterCart={buildFilteredCartByStore(store.id, false)}
                storeList={storeList}
                mode={mode}
                setNoReplace={setNoReplace}
                {...props}
              />
            </TabsContent>
          );
        })}
        {hasUnavailableProducts && (
          <TabsContent value={unavailableTabValue}>
            <p className="text-sm my-2 font-semibold">
              No se han encontrado en esta tienda:
            </p>
            <ProductTable
              className="mt-0 opacity-60"
              cart={opt_cart}
              filterCart={props.noFoundCart || []}
              storeList={storeList}
              mode={mode}
              setNoReplace={setNoReplace}
              noClick={true}
              notFoundsFlag={true}
              {...props}
            />
          </TabsContent>
        )}
      </Tabs>
    );
  },
);

